// ZIP Code Lookup Service

export interface ZipCodeResult {
  zipCode: string;
  city: string;
  state: string;
  success: boolean;
  error?: string;
}

export class ZipCodeLookup {
  private static API_BASE_URL = 'https://api.zippopotam.us';

  /**
   * Lookup ZIP code for a given address
   * @param address - Full address string
   * @returns Promise with ZIP code result
   */
  static async lookupZipCode(address: string): Promise<ZipCodeResult> {
    try {
      // First, try to extract state and city from the address
      const stateCity = this.extractStateCity(address);
      
      if (!stateCity.state || !stateCity.city) {
        return {
          zipCode: '',
          city: '',
          state: '',
          success: false,
          error: 'Could not extract state and city from address'
        };
      }

      // Use the Zippopotam.us API to get ZIP codes for the city/state
      const encodedCity = encodeURIComponent(stateCity.city);
      const response = await fetch(
        `${this.API_BASE_URL}/us/${stateCity.state}/${encodedCity}`
      );

      if (!response.ok) {
        // Fallback: try with just the state
        const stateResponse = await fetch(
          `${this.API_BASE_URL}/us/${stateCity.state}`
        );
        
        if (!stateResponse.ok) {
          return {
            zipCode: '',
            city: stateCity.city,
            state: stateCity.state,
            success: false,
            error: 'Could not find ZIP codes for this location'
          };
        }
        
        const stateData = await stateResponse.json();
        // Return the first ZIP code found for the state
        const firstZip = stateData.places?.[0]?.['post code'];
        
        return {
          zipCode: firstZip || '',
          city: stateCity.city,
          state: stateCity.state,
          success: !!firstZip
        };
      }

      const data = await response.json();
      const zipCodes = data.places?.map((place: any) => place['post code']) || [];
      
      if (zipCodes.length === 0) {
        return {
          zipCode: '',
          city: stateCity.city,
          state: stateCity.state,
          success: false,
          error: 'No ZIP codes found for this city'
        };
      }

      // Return the first ZIP code (most common)
      return {
        zipCode: zipCodes[0],
        city: stateCity.city,
        state: stateCity.state,
        success: true
      };

    } catch (error) {
      console.error('ZIP code lookup error:', error);
      return {
        zipCode: '',
        city: '',
        state: '',
        success: false,
        error: 'Failed to lookup ZIP code'
      };
    }
  }

  /**
   * Extract state and city from address string
   * @param address - Full address string
   * @returns Object with state and city
   */
  private static extractStateCity(address: string): { state: string; city: string } {
    const cleanAddress = address.trim();
    
    // Common US state abbreviations
    const stateAbbreviations = [
      'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
      'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
      'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
      'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
      'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
    ];

    // Find state in the address
    let state = '';
    let city = '';
    
    for (const stateAbbr of stateAbbreviations) {
      const statePattern = new RegExp(`\\b${stateAbbr}\\b`, 'i');
      const stateMatch = cleanAddress.match(statePattern);
      
      if (stateMatch) {
        state = stateAbbr.toUpperCase();
        const beforeState = cleanAddress.substring(0, stateMatch.index).trim();
        
        // Extract city (usually the last part before state)
        const parts = beforeState.split(',').map(part => part.trim()).filter(part => part.length > 0);
        if (parts.length > 1) {
          city = parts[parts.length - 1];
        } else {
          // If no comma, try to extract city from the end
          const words = beforeState.split(' ');
          if (words.length > 2) {
            // Assume last word or two might be city
            city = words.slice(-2).join(' ');
          } else {
            city = beforeState;
          }
        }
        
        // Clean up city name (remove extra spaces, handle abbreviations)
        city = city.replace(/\s+/g, ' ').trim();
        if (city.toLowerCase().includes('st.') || city.toLowerCase().includes('saint')) {
          city = city.replace(/^st\.?\s*/i, 'St. ');
        }
        
        break;
      }
    }
    
    return { state, city };
  }

  /**
   * Get a fallback ZIP code for common cities
   * @param city - City name
   * @param state - State abbreviation
   * @returns ZIP code or empty string
   */
  static getFallbackZipCode(city: string, state: string): string {
    const fallbackZips: Record<string, Record<string, string>> = {
      'MO': {
        'St. Peters': '63376',
        'Saint Peters': '63376',
        'St Peters': '63376',
        'Saint Louis': '63101',
        'Kansas City': '64101',
        'Springfield': '65801',
        'Columbia': '65201'
      },
      'TN': {
        'Nashville': '37201',
        'Memphis': '38101',
        'Knoxville': '37901',
        'Chattanooga': '37401'
      },
      'CA': {
        'Los Angeles': '90001',
        'San Francisco': '94101',
        'San Diego': '92101'
      },
      'NY': {
        'New York': '10001',
        'Buffalo': '14201',
        'Albany': '12201'
      }
    };

    return fallbackZips[state]?.[city] || '';
  }
} 